import type { YearBand } from '../types';

import { isoWeekYear, startOfIsoWeek } from './axisTicks';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** The year each piece of the axis falls in: by ISO week-year for weeks, else calendar year. */
function pieces(startMs: number, endMs: number, unit: 'week' | 'month') {
    const out: { year: number; from: number; to: number }[] = [];
    if (unit === 'week') {
        for (
            let monday = startOfIsoWeek(new Date(startMs));
            monday.getTime() < endMs;
            monday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 7)
        ) {
            const year = isoWeekYear(monday);
            out.push({ year, from: monday.getTime(), to: monday.getTime() + WEEK_MS });
        }
        return out;
    }
    for (
        let year = new Date(startMs).getFullYear();
        new Date(year, 0, 1).getTime() < endMs;
        year++
    ) {
        out.push({
            year,
            from: new Date(year, 0, 1).getTime(),
            to: new Date(year + 1, 0, 1).getTime(),
        });
    }
    return out;
}

/**
 * The years a week or month axis spans, for the year row over its labels: weeks 43–52 and
 * February to October come round every year, so a long cycle names the year wherever it is
 * scrolled to. Weeks group by ISO week-year, so week 1 sits under the year it belongs to.
 */
export function buildYearBands(startMs: number, endMs: number, unit: 'week' | 'month'): YearBand[] {
    const span = endMs - startMs;
    const bands: YearBand[] = [];
    for (const piece of pieces(startMs, endMs, unit)) {
        const from = Math.max(piece.from, startMs);
        const to = Math.min(piece.to, endMs);
        if (to <= from) continue;
        const last = bands[bands.length - 1];
        if (last?.year === piece.year) {
            last.widthPct += ((to - from) / span) * 100;
        } else {
            bands.push({
                key: `year-${piece.year}`,
                year: piece.year,
                leftPct: ((from - startMs) / span) * 100,
                widthPct: ((to - from) / span) * 100,
            });
        }
    }
    return bands;
}

/**
 * The tick whose label the pinned name column would cut ("K 42"). Labels stick just right of
 * the column, so the tick straddling it keeps its label while the visible part of the tick
 * still holds the whole label (`labelPx`, its rendered width with its inset); only once that
 * part is narrower is its key returned, for the label to hide. `scrollLeft` is in track
 * pixels, the visible track's left edge.
 */
export function coveredTickKey(
    ticks: readonly { key: string; leftPct: number; widthPct: number }[],
    trackPx: number,
    scrollLeft: number,
    labelPx: (key: string) => number
): string | null {
    if (scrollLeft <= 0) return null;
    const straddling = ticks.find((tick) => {
        const left = (tick.leftPct / 100) * trackPx;
        const right = ((tick.leftPct + tick.widthPct) / 100) * trackPx;
        return left < scrollLeft && right > scrollLeft;
    });
    if (!straddling) return null;
    const right = ((straddling.leftPct + straddling.widthPct) / 100) * trackPx;
    return right - scrollLeft < labelPx(straddling.key) ? straddling.key : null;
}
